import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle2,
  Facebook,
  Globe,
  Loader2,
  MessageSquare,
  Plus,
  RefreshCw,
  Server,
  ShoppingCart,
  Trash2,
  Zap,
} from 'lucide-react';
import {
  useEduzzStats,
  useProductMaps,
  useUpsertProductMap,
  useMetaAdsAccounts,
  useMetaAdsStats,
  useCreateMetaAdsAccount,
  useSyncMetaAds,
  useDeleteMetaAdsAccount,
  useMCStats,
  useUpsertMCAgent,
  usePingMCAgent,
  useDeleteMCAgent,
  useEvolutionInstances,
  useUpsertEvolutionInstance,
  useSyncEvolution,
  useDeleteEvolutionInstance,
  useSocialStats,
  useUpsertSocialAccount,
  useSyncSocial,
  useDeleteSocialAccount,
  formatBRL,
  formatNumber,
  type SocialPlatform,
} from '@/hooks/useIntegrations';
import { useAuthStore } from '@/stores/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export function IntegracoesPage(): JSX.Element {
  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">Integrações</h1>
        <p className="text-sm text-muted-foreground">
          Eduzz (vendas) · Meta Ads (tráfego) — configuração e métricas agregadas.
        </p>
      </div>

      <Tabs defaultValue="eduzz">
        <TabsList>
          <TabsTrigger value="eduzz" className="gap-1">
            <ShoppingCart className="h-3.5 w-3.5" /> Eduzz
          </TabsTrigger>
          <TabsTrigger value="meta-ads" className="gap-1">
            <Facebook className="h-3.5 w-3.5" /> Meta Ads
          </TabsTrigger>
          <TabsTrigger value="mc" className="gap-1">
            <Server className="h-3.5 w-3.5" /> Marketing Command
          </TabsTrigger>
          <TabsTrigger value="evolution" className="gap-1">
            <MessageSquare className="h-3.5 w-3.5" /> Evolution
          </TabsTrigger>
          <TabsTrigger value="social" className="gap-1">
            <Globe className="h-3.5 w-3.5" /> Redes sociais
          </TabsTrigger>
        </TabsList>

        <TabsContent value="eduzz">
          <EduzzPanel />
        </TabsContent>

        <TabsContent value="meta-ads">
          <MetaAdsPanel />
        </TabsContent>

        <TabsContent value="mc">
          <MarketingCommandPanel />
        </TabsContent>

        <TabsContent value="evolution">
          <EvolutionPanel />
        </TabsContent>

        <TabsContent value="social">
          <SocialPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ============================================================================
// Eduzz
// ============================================================================
function EduzzPanel(): JSX.Element {
  const { data: stats, isLoading } = useEduzzStats();
  const { data: mapsData } = useProductMaps();
  const upsertMap = useUpsertProductMap();

  if (isLoading || !stats) {
    return <p className="text-sm text-muted-foreground">Carregando stats…</p>;
  }

  return (
    <div className="space-y-4">
      {/* Cards de vendas */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          title="Vendas hoje"
          value={formatBRL(stats.hoje.valor_cents)}
          subtitle={`${stats.hoje.vendas} vendas`}
        />
        <StatCard
          title="Vendas ontem"
          value={formatBRL(stats.ontem.valor_cents)}
          subtitle={`${stats.ontem.vendas} vendas`}
        />
        <StatCard
          title="Últimos 30 dias"
          value={formatBRL(stats.ultimos_30d.valor_cents)}
          subtitle={`${stats.ultimos_30d.vendas} vendas`}
        />
        <StatCard
          title="MRR (recorrência 30d)"
          value={formatBRL(stats.mrr_cents)}
          subtitle="Assinaturas ativas"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Top produtos */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Top 5 produtos (30d)</CardTitle>
          </CardHeader>
          <CardContent>
            {stats.top_produtos_30d.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                Nenhuma venda nos últimos 30 dias. Aguardando webhook Eduzz.
              </p>
            ) : (
              <ul className="space-y-2">
                {stats.top_produtos_30d.map((p) => (
                  <li key={p.produto_nome} className="flex items-center justify-between text-sm">
                    <span className="truncate">{p.produto_nome}</span>
                    <span className="text-muted-foreground font-mono text-xs">
                      {formatBRL(p.valor_cents)} · {p.vendas}x
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Reembolsos + não mapeados */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Alertas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm">Reembolsos 30d</span>
              <Badge variant="secondary" className="text-xs">
                {formatBRL(stats.reembolsos_30d.valor_cents)} · {stats.reembolsos_30d.count}x
              </Badge>
            </div>
            {stats.produtos_nao_mapeados.length > 0 && (
              <>
                <div className="flex items-center gap-1 mt-2 mb-1">
                  <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                  <span className="text-xs font-medium">Produtos sem mapeamento</span>
                </div>
                <ul className="space-y-1">
                  {stats.produtos_nao_mapeados.slice(0, 5).map((p) => (
                    <li key={p.eduzz_product_id} className="flex items-center justify-between text-xs">
                      <code className="bg-muted px-1 rounded">{p.eduzz_product_id}</code>
                      <span className="text-muted-foreground">{p.count} vendas</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Mapeamentos configurados */}
      {mapsData && mapsData.maps.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Produtos mapeados</CardTitle>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground">
                <tr>
                  <th className="text-left py-1">Produto Eduzz</th>
                  <th className="text-left py-1">Curso na Central</th>
                </tr>
              </thead>
              <tbody>
                {mapsData.maps.map((m) => (
                  <tr key={m.eduzz_product_id} className="border-t border-border">
                    <td className="py-1 font-mono text-xs">{m.eduzz_product_id}</td>
                    <td className="py-1">
                      {m.curso_central_id ? (
                        <Link
                          to={`/items/${m.curso_central_id}`}
                          className="text-primary hover:underline"
                        >
                          {m.curso_nome ?? `Curso #${m.curso_central_id}`}
                        </Link>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Config status */}
      <Card className="border-dashed">
        <CardContent className="pt-4 text-xs text-muted-foreground">
          <p className="mb-1">
            <strong>Webhook URL (Eduzz):</strong>{' '}
            <code className="bg-muted px-1 rounded">POST /api/integrations/eduzz/webhook</code>
          </p>
          <p>
            Configure <code>EDUZZ_WEBHOOK_SECRET</code> no <code>.env</code> com o secret partilhado
            pra validação HMAC-SHA256. Mapeie produtos novos quando aparecerem no alerta acima.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================================
// Meta Ads
// ============================================================================
function MetaAdsPanel(): JSX.Element {
  const { data: accountsData } = useMetaAdsAccounts();
  const { data: stats } = useMetaAdsStats();
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === 'admin';
  const sync = useSyncMetaAds();
  const del = useDeleteMetaAdsAccount();
  const [addOpen, setAddOpen] = useState(false);

  async function handleSync(): Promise<void> {
    await sync.mutateAsync();
  }

  return (
    <div className="space-y-4">
      {/* Cards topo */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard
            title="Gasto hoje"
            value={formatBRL(stats.hoje.gasto_cents)}
            subtitle={`${stats.hoje.leads} leads · ${stats.hoje.cliques} cliques`}
          />
          <StatCard
            title="Gasto ontem"
            value={formatBRL(stats.ontem.gasto_cents)}
            subtitle={`${stats.ontem.leads} leads`}
          />
          <StatCard
            title="Gasto 7d"
            value={formatBRL(stats.ultimos_7d.gasto_cents)}
            subtitle={`${stats.ultimos_7d.leads} leads`}
          />
          <StatCard
            title="Accounts ativas"
            value={String(accountsData?.accounts.filter((a) => a.ativa).length ?? 0)}
            subtitle={`${accountsData?.accounts.length ?? 0} total`}
          />
        </div>
      )}

      <div className="flex items-center justify-between">
        <h2 className="font-medium text-sm">Ad accounts monitoradas</h2>
        <div className="flex gap-2">
          {isAdmin && (
            <>
              <Button size="sm" variant="outline" onClick={handleSync} disabled={sync.isPending}>
                {sync.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <RefreshCw className="h-3.5 w-3.5" />
                )}{' '}
                Sincronizar
              </Button>
              <Button size="sm" onClick={() => setAddOpen(true)}>
                <Plus className="h-3.5 w-3.5" /> Nova account
              </Button>
            </>
          )}
        </div>
      </div>

      {!accountsData || accountsData.accounts.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="pt-6 text-center text-sm text-muted-foreground">
            Nenhuma ad account cadastrada. Clique em "+ Nova account" pra começar.
            <br />
            Ana Lazarotto sugerida: <code className="bg-muted px-1 rounded">act_217134671443090</code>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs text-muted-foreground">
                <tr>
                  <th className="text-left px-3 py-2">Account</th>
                  <th className="text-left px-3 py-2">Ad Account ID</th>
                  <th className="text-right px-3 py-2">Gasto 7d</th>
                  <th className="text-right px-3 py-2">Leads 7d</th>
                  <th className="text-right px-3 py-2">Último sync</th>
                  <th className="text-right px-3 py-2">Status</th>
                  {isAdmin && <th className="px-3 py-2" />}
                </tr>
              </thead>
              <tbody>
                {accountsData.accounts.map((a) => {
                  const accStats = stats?.por_account.find((p) => p.ad_account_id === a.ad_account_id);
                  return (
                    <tr key={a.id} className="border-t border-border">
                      <td className="px-3 py-2 font-medium">{a.nome}</td>
                      <td className="px-3 py-2 font-mono text-xs text-muted-foreground">
                        {a.ad_account_id}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-xs">
                        {accStats ? formatBRL(accStats.gasto_7d_cents) : '—'}
                      </td>
                      <td className="px-3 py-2 text-right text-xs">{accStats?.leads_7d ?? 0}</td>
                      <td className="px-3 py-2 text-right text-xs text-muted-foreground">
                        {a.ultimo_sync_em
                          ? new Date(a.ultimo_sync_em).toLocaleString('pt-BR')
                          : 'nunca'}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {a.ultimo_erro ? (
                          <Badge variant="destructive" className="text-[10px]">
                            erro
                          </Badge>
                        ) : a.ativa ? (
                          <Badge className="bg-emerald-500/20 text-emerald-500 text-[10px]">
                            <CheckCircle2 className="h-3 w-3" /> ativa
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="text-[10px]">
                            pausada
                          </Badge>
                        )}
                      </td>
                      {isAdmin && (
                        <td className="px-3 py-2 text-right">
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() =>
                              confirm(`Remover "${a.nome}"?`) && del.mutate(a.id)
                            }
                            title="Remover"
                          >
                            <Trash2 className="h-3.5 w-3.5 text-destructive" />
                          </Button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <Card className="border-dashed">
        <CardContent className="pt-4 text-xs text-muted-foreground">
          Configure <code>META_APP_ID</code> + <code>META_APP_SECRET</code> no <code>.env</code>.
          Gere token long-lived de cada ad account com permissão <code>ads_read</code> e cole no
          formulário. Tokens são criptografados em repouso com AES-256-GCM.
        </CardContent>
      </Card>

      <AddAdAccountModal open={addOpen} onOpenChange={setAddOpen} />
    </div>
  );
}

function AddAdAccountModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}): JSX.Element {
  const [nome, setNome] = useState('');
  const [adAccountId, setAdAccountId] = useState('');
  const [token, setToken] = useState('');
  const create = useCreateMetaAdsAccount();
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    try {
      await create.mutateAsync({
        nome,
        ad_account_id: adAccountId.startsWith('act_') ? adAccountId : `act_${adAccountId}`,
        token,
      });
      setNome('');
      setAdAccountId('');
      setToken('');
      onOpenChange(false);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Nova ad account Meta</DialogTitle>
            <DialogDescription>
              Cadastre um ad account pra monitorar gasto + leads automaticamente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 my-4">
            <div>
              <label htmlFor="nome" className="block text-sm font-medium mb-1">
                Nome
              </label>
              <Input
                id="nome"
                required
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Ana Lazarotto"
              />
            </div>
            <div>
              <label htmlFor="adid" className="block text-sm font-medium mb-1">
                Ad Account ID
              </label>
              <Input
                id="adid"
                required
                value={adAccountId}
                onChange={(e) => setAdAccountId(e.target.value)}
                placeholder="act_217134671443090"
              />
            </div>
            <div>
              <label htmlFor="token" className="block text-sm font-medium mb-1">
                Access Token (long-lived)
              </label>
              <Input
                id="token"
                required
                type="password"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="EAA..."
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                Obtenha em developers.facebook.com → permissão ads_read. Será criptografado.
              </p>
            </div>
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2">
                {error}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? 'Salvando…' : 'Cadastrar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function StatCard({
  title,
  value,
  subtitle,
}: {
  title: string;
  value: string;
  subtitle?: string;
}): JSX.Element {
  return (
    <Card>
      <CardContent className="pt-4">
        <div className="text-xs text-muted-foreground">{title}</div>
        <div className="text-xl font-semibold mt-1">{value}</div>
        {subtitle && <div className="text-[10px] text-muted-foreground mt-0.5">{subtitle}</div>}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Marketing Command — 5 agentes REST (health check)
// ============================================================================
function MarketingCommandPanel(): JSX.Element {
  const { data, isLoading } = useMCStats();
  const upsert = useUpsertMCAgent();
  const ping = usePingMCAgent();
  const del = useDeleteMCAgent();
  const role = useAuthStore((s) => s.user?.role);
  const canAdmin = role === 'admin' || role === 'editor';
  const [open, setOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Carregando agentes...
      </div>
    );
  }

  const agents = data?.agents ?? [];

  return (
    <div className="space-y-4 pt-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard title="Agentes ativos" value={formatNumber(data?.total ?? 0)} />
        <StatCard title="OK" value={formatNumber(data?.ok ?? 0)} subtitle="último check" />
        <StatCard title="Erros" value={formatNumber((data?.erro ?? 0) + (data?.timeout ?? 0))} />
        <StatCard
          title="Latência média"
          value={data?.latencia_media_ms ? `${Math.round(data.latencia_media_ms)}ms` : '—'}
        />
      </div>

      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold">Agentes cadastrados</h2>
        {canAdmin && (
          <Button size="sm" onClick={() => setOpen(true)}>
            <Plus className="mr-1 h-3 w-3" /> Adicionar
          </Button>
        )}
      </div>

      {agents.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            Nenhum agente cadastrado. Cadastre os 5 do Marketing Command (novo-lead, mentorada-status,
            agenda-hoje, aprovar-batch-alice, whatsapp) apontando pra{' '}
            <code>comando.iacomtata.com.br</code>.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {agents.map((a) => (
            <Card key={a.id}>
              <CardContent className="flex items-center justify-between pt-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{a.nome}</span>
                    <Badge variant="outline" className="font-mono text-xs">
                      {a.agent_key}
                    </Badge>
                    {a.ultimo_status === 'ok' && (
                      <Badge className="bg-emerald-600">
                        <CheckCircle2 className="mr-1 h-3 w-3" /> OK
                      </Badge>
                    )}
                    {a.ultimo_status === 'erro' && <Badge variant="destructive">Erro</Badge>}
                    {a.ultimo_status === 'timeout' && <Badge variant="destructive">Timeout</Badge>}
                    {!a.ultimo_status && <Badge variant="outline">Sem check</Badge>}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {a.metodo} {a.base_url}
                    {a.endpoint}
                    {a.latencia_ms !== null && <> · {a.latencia_ms}ms</>}
                    {a.ultimo_erro && <> · {a.ultimo_erro}</>}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => ping.mutate(a.id)}
                    disabled={ping.isPending}
                  >
                    <Zap className="mr-1 h-3 w-3" /> Ping
                  </Button>
                  {canAdmin && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        if (confirm(`Remover ${a.nome}?`)) del.mutate(a.id);
                      }}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <MCAgentModal open={open} onOpenChange={setOpen} upsert={upsert} />
    </div>
  );
}

function MCAgentModal({
  open,
  onOpenChange,
  upsert,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  upsert: ReturnType<typeof useUpsertMCAgent>;
}): JSX.Element {
  const [form, setForm] = useState({
    agent_key: '',
    nome: '',
    base_url: 'https://comando.iacomtata.com.br',
    endpoint: '',
    metodo: 'GET' as 'GET' | 'POST',
    auth_header: '',
    auth_token: '',
  });
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    try {
      await upsert.mutateAsync({
        agent_key: form.agent_key,
        nome: form.nome,
        base_url: form.base_url,
        endpoint: form.endpoint,
        metodo: form.metodo,
        auth_header: form.auth_header || null,
        auth_token: form.auth_token || null,
      });
      onOpenChange(false);
      setForm({
        agent_key: '',
        nome: '',
        base_url: 'https://comando.iacomtata.com.br',
        endpoint: '',
        metodo: 'GET',
        auth_header: '',
        auth_token: '',
      });
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={onSubmit}>
          <DialogHeader>
            <DialogTitle>Novo agente Marketing Command</DialogTitle>
            <DialogDescription>Endpoint REST monitorado via health check.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <div>
              <label className="text-xs font-semibold">Agent key</label>
              <Input
                placeholder="novo-lead, mentorada-status, ..."
                value={form.agent_key}
                onChange={(e) => setForm({ ...form, agent_key: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="text-xs font-semibold">Nome</label>
              <Input
                placeholder="Agente Novo Lead"
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                required
              />
            </div>
            <div className="grid grid-cols-[1fr_100px] gap-2">
              <div>
                <label className="text-xs font-semibold">Base URL</label>
                <Input
                  value={form.base_url}
                  onChange={(e) => setForm({ ...form, base_url: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="text-xs font-semibold">Método</label>
                <select
                  value={form.metodo}
                  onChange={(e) =>
                    setForm({ ...form, metodo: e.target.value as 'GET' | 'POST' })
                  }
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="GET">GET</option>
                  <option value="POST">POST</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold">Endpoint</label>
              <Input
                placeholder="/api/novo-lead"
                value={form.endpoint}
                onChange={(e) => setForm({ ...form, endpoint: e.target.value })}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-semibold">Auth header (opcional)</label>
                <Input
                  placeholder="X-API-Key"
                  value={form.auth_header}
                  onChange={(e) => setForm({ ...form, auth_header: e.target.value })}
                />
              </div>
              <div>
                <label className="text-xs font-semibold">Token (opcional)</label>
                <Input
                  type="password"
                  value={form.auth_token}
                  onChange={(e) => setForm({ ...form, auth_token: e.target.value })}
                />
              </div>
            </div>
            {error && (
              <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={upsert.isPending}>
              {upsert.isPending ? 'Salvando…' : 'Salvar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Evolution API — WhatsApp
// ============================================================================
function EvolutionPanel(): JSX.Element {
  const { data, isLoading } = useEvolutionInstances();
  const sync = useSyncEvolution();
  const upsert = useUpsertEvolutionInstance();
  const del = useDeleteEvolutionInstance();
  const role = useAuthStore((s) => s.user?.role);
  const canAdmin = role === 'admin';
  const [open, setOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Carregando instâncias...
      </div>
    );
  }

  const instances = data?.instances ?? [];
  const conectadas = instances.filter((i) => i.ultimo_status === 'open').length;
  const msgsHoje = instances.reduce((s, i) => s + i.mensagens_hoje, 0);

  return (
    <div className="space-y-4 pt-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard title="Instâncias" value={formatNumber(instances.length)} />
        <StatCard title="Conectadas" value={formatNumber(conectadas)} subtitle="/ total" />
        <StatCard title="Mensagens hoje" value={formatNumber(msgsHoje)} />
        <StatCard
          title="Último sync"
          value={
            instances[0]?.ultimo_sync_em
              ? new Date(instances[0].ultimo_sync_em).toLocaleTimeString('pt-BR')
              : '—'
          }
        />
      </div>

      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold">Instâncias</h2>
        <div className="flex gap-2">
          {canAdmin && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => sync.mutate()}
              disabled={sync.isPending}
            >
              <RefreshCw className={sync.isPending ? 'mr-1 h-3 w-3 animate-spin' : 'mr-1 h-3 w-3'} />
              Sync
            </Button>
          )}
          {canAdmin && (
            <Button size="sm" onClick={() => setOpen(true)}>
              <Plus className="mr-1 h-3 w-3" /> Nova instância
            </Button>
          )}
        </div>
      </div>

      {instances.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            Nenhuma instância Evolution cadastrada. Cadastre apontando pra{' '}
            <code>evo.mentoriaimperioia.com.br</code>.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {instances.map((i) => (
            <Card key={i.id}>
              <CardContent className="flex items-center justify-between pt-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="h-4 w-4 text-muted-foreground" />
                    <span className="font-semibold">{i.instance_name}</span>
                    {i.numero && <Badge variant="outline">{i.numero}</Badge>}
                    {i.ultimo_status === 'open' && (
                      <Badge className="bg-emerald-600">Conectado</Badge>
                    )}
                    {i.ultimo_status === 'connecting' && (
                      <Badge className="bg-amber-500">Conectando</Badge>
                    )}
                    {i.ultimo_status === 'close' && <Badge variant="destructive">Desconectado</Badge>}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {i.base_url} · {i.mensagens_hoje} msgs hoje
                    {i.ultimo_erro && <> · {i.ultimo_erro}</>}
                  </div>
                </div>
                {canAdmin && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      if (confirm(`Remover ${i.instance_name}?`)) del.mutate(i.id);
                    }}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <EvolutionModal open={open} onOpenChange={setOpen} upsert={upsert} />
    </div>
  );
}

function EvolutionModal({
  open,
  onOpenChange,
  upsert,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  upsert: ReturnType<typeof useUpsertEvolutionInstance>;
}): JSX.Element {
  const [form, setForm] = useState({
    instance_name: '',
    base_url: 'https://evo.mentoriaimperioia.com.br',
    api_key: '',
    numero: '',
    descricao: '',
  });
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    try {
      await upsert.mutateAsync({
        instance_name: form.instance_name,
        base_url: form.base_url,
        api_key: form.api_key,
        numero: form.numero || null,
        descricao: form.descricao || null,
      });
      onOpenChange(false);
      setForm({
        instance_name: '',
        base_url: 'https://evo.mentoriaimperioia.com.br',
        api_key: '',
        numero: '',
        descricao: '',
      });
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={onSubmit}>
          <DialogHeader>
            <DialogTitle>Nova instância Evolution</DialogTitle>
            <DialogDescription>API key é armazenada criptografada (AES-256-GCM).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <div>
              <label className="text-xs font-semibold">Instance name</label>
              <Input
                value={form.instance_name}
                onChange={(e) => setForm({ ...form, instance_name: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="text-xs font-semibold">Base URL</label>
              <Input
                value={form.base_url}
                onChange={(e) => setForm({ ...form, base_url: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="text-xs font-semibold">API Key</label>
              <Input
                type="password"
                value={form.api_key}
                onChange={(e) => setForm({ ...form, api_key: e.target.value })}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-semibold">Número (opcional)</label>
                <Input
                  placeholder="+5511999999999"
                  value={form.numero}
                  onChange={(e) => setForm({ ...form, numero: e.target.value })}
                />
              </div>
              <div>
                <label className="text-xs font-semibold">Descrição</label>
                <Input
                  value={form.descricao}
                  onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                />
              </div>
            </div>
            {error && (
              <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={upsert.isPending}>
              {upsert.isPending ? 'Salvando…' : 'Salvar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Redes Sociais
// ============================================================================
const PLATFORM_LABELS: Record<SocialPlatform, string> = {
  youtube: 'YouTube',
  facebook: 'Facebook',
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
  tiktok: 'TikTok',
};

function SocialPanel(): JSX.Element {
  const { data, isLoading } = useSocialStats();
  const sync = useSyncSocial();
  const upsert = useUpsertSocialAccount();
  const del = useDeleteSocialAccount();
  const role = useAuthStore((s) => s.user?.role);
  const canAdmin = role === 'admin';
  const [open, setOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Carregando contas...
      </div>
    );
  }

  const accounts = data?.por_plataforma ?? [];
  const totalSeguidores = accounts.reduce((s, a) => s + (a.seguidores ?? 0), 0);

  return (
    <div className="space-y-4 pt-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard title="Contas" value={formatNumber(data?.total ?? 0)} />
        <StatCard
          title="API habilitada"
          value={formatNumber(accounts.filter((a) => a.api_habilitada).length)}
        />
        <StatCard title="Seguidores total" value={formatNumber(totalSeguidores)} />
        <StatCard
          title="Último sync"
          value={
            accounts.find((a) => a.ultimo_sync_em)?.ultimo_sync_em
              ? new Date(accounts.find((a) => a.ultimo_sync_em)!.ultimo_sync_em!).toLocaleDateString(
                  'pt-BR',
                )
              : '—'
          }
        />
      </div>

      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold">Contas sociais</h2>
        <div className="flex gap-2">
          {canAdmin && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => sync.mutate()}
              disabled={sync.isPending}
            >
              <RefreshCw className={sync.isPending ? 'mr-1 h-3 w-3 animate-spin' : 'mr-1 h-3 w-3'} />
              Sync API
            </Button>
          )}
          {canAdmin && (
            <Button size="sm" onClick={() => setOpen(true)}>
              <Plus className="mr-1 h-3 w-3" /> Nova conta
            </Button>
          )}
        </div>
      </div>

      {accounts.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            Nenhuma rede social cadastrada. Cadastre YouTube/Facebook com token pra puxar métricas, ou
            adicione IG/LinkedIn/TikTok como link-only.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-2 md:grid-cols-2">
          {accounts.map((a, idx) => (
            <Card key={`${a.plataforma}-${idx}`}>
              <CardContent className="pt-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{PLATFORM_LABELS[a.plataforma]}</Badge>
                      {a.api_habilitada ? (
                        <Badge className="bg-emerald-600">API</Badge>
                      ) : (
                        <Badge variant="outline">Link-only</Badge>
                      )}
                    </div>
                    <div className="mt-1 font-semibold">{a.nome}</div>
                    <a
                      href={a.url_publica}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-0.5 block text-xs text-primary hover:underline"
                    >
                      {a.url_publica}
                    </a>
                  </div>
                  {a.seguidores !== null && (
                    <div className="text-right">
                      <div className="text-2xl font-bold">{formatNumber(a.seguidores)}</div>
                      <div className="text-[10px] text-muted-foreground">seguidores</div>
                    </div>
                  )}
                </div>
                {a.posts !== null && a.posts !== undefined && (
                  <div className="mt-2 text-xs text-muted-foreground">
                    {formatNumber(a.posts)} publicações
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <SocialModal open={open} onOpenChange={setOpen} upsert={upsert} />
    </div>
  );
}

function SocialModal({
  open,
  onOpenChange,
  upsert,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  upsert: ReturnType<typeof useUpsertSocialAccount>;
}): JSX.Element {
  const [form, setForm] = useState<{
    plataforma: SocialPlatform;
    nome: string;
    identificador: string;
    url_publica: string;
    api_token: string;
  }>({
    plataforma: 'youtube',
    nome: '',
    identificador: '',
    url_publica: '',
    api_token: '',
  });
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    try {
      await upsert.mutateAsync({
        plataforma: form.plataforma,
        nome: form.nome,
        identificador: form.identificador,
        url_publica: form.url_publica,
        api_token: form.api_token || null,
      });
      onOpenChange(false);
      setForm({
        plataforma: 'youtube',
        nome: '',
        identificador: '',
        url_publica: '',
        api_token: '',
      });
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={onSubmit}>
          <DialogHeader>
            <DialogTitle>Nova conta social</DialogTitle>
            <DialogDescription>
              Com token, stats são puxadas automaticamente. Sem token, vira link-only.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <div>
              <label className="text-xs font-semibold">Plataforma</label>
              <select
                value={form.plataforma}
                onChange={(e) => setForm({ ...form, plataforma: e.target.value as SocialPlatform })}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="youtube">YouTube</option>
                <option value="facebook">Facebook</option>
                <option value="instagram">Instagram</option>
                <option value="linkedin">LinkedIn</option>
                <option value="tiktok">TikTok</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold">Nome</label>
              <Input
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="text-xs font-semibold">Identificador</label>
              <Input
                placeholder="channelId (YT), pageId (FB), @username (IG/TikTok)..."
                value={form.identificador}
                onChange={(e) => setForm({ ...form, identificador: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="text-xs font-semibold">URL pública</label>
              <Input
                type="url"
                value={form.url_publica}
                onChange={(e) => setForm({ ...form, url_publica: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="text-xs font-semibold">
                API Token (YT Data API Key / FB Page Access Token)
              </label>
              <Input
                type="password"
                placeholder="Deixe vazio pra link-only"
                value={form.api_token}
                onChange={(e) => setForm({ ...form, api_token: e.target.value })}
              />
            </div>
            {error && (
              <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={upsert.isPending}>
              {upsert.isPending ? 'Salvando…' : 'Salvar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
